import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  Breadcrumbs,
  Link,
  Divider,
  FormGroup,
  Snackbar,
  Chip,
  MenuList,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  QuestionAnswer as VerifyIcon,
} from "@mui/icons-material";
import axios from "axios";
import { decryptText, encryptText, encryptAppId } from "../../utils/encryption";
import FileUploadComponent from "../FileUploadComponent";
import { getScrutinyRemarks, getClauseNo } from "../../services/api";
import paymentService from "../../services/paymentService";
import {
  insertDANC,
  updateDANCBySourceIdNewAppType,
  getDANC,
} from "../../services/apiCalibration";
import { TerminateApplicationDialog } from "../../components";


// AdequacyCheckSection Component for Change In CAB Premises
const AdequacyCheckSection = React.memo(
  ({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyChecks,
    handleAdequacyCheck,
    handleAdequacyRemarks,
    handleAdequacySubmit,
    handleAdequacySubmitted,
    loading,
    currentUser = "System User",
    currentUserId = localStorage.getItem("orgId") || 0,
  }) => {
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState("");
    const [dancSuccess, setDancSuccess] = useState("");
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [loadingRemarks, setLoadingRemarks] = useState(false);
    const [selectedRemarksOptions, setSelectedRemarksOptions] = useState([]);
    const [isCheckboxDropdownOpen, setIsCheckboxDropdownOpen] = useState(false);

    const currentUserTypeId = localStorage.getItem("userTypeId");
    const expectedNCType = "Document Adequacy";

    // Load existing DANC data on component mount
    useEffect(() => {
      const loadExistingDANCData = async () => {
        if (!orgId || !appId || dataLoaded) return;

        if (currentUserTypeId !== "4") return;
        if (!["15", "15a", "15b"].includes(applicationStage)) return;

        try {
          setDancLoading(true);

          const response = await getDANC({
            orgid: orgId,
            appid: appId,
            sectionName: "Change In CAB Premises",
            subsectionName: title,
            isactive: 1,
          });

          if (response.data.success && response.data.data?.length > 0) {
            const filteredRecords = response.data.data.filter(
              (record) => record.NCtype === expectedNCType,
            );

            if (filteredRecords.length > 0) {
              const recordData = filteredRecords[0];
              console.log(`✅ Loading ${title} DANC:`, recordData);
              setExistingDANCRecord(recordData);

              if (recordData.InformationStatus) {
                handleAdequacyCheck(sectionId, recordData.InformationStatus);
              }

              const remarks =
                recordData.InformationProvided || recordData.Observation || "";
              if (remarks) {
                handleAdequacyRemarks(sectionId, remarks);
              }

              // Mark as submitted since existing record found
              if (handleAdequacySubmitted) {
                handleAdequacySubmitted(sectionId, true);
              }
            }
          }
          setDataLoaded(true);
        } catch (error) {
          console.log("Error loading DANC data:", error.message);
          setDataLoaded(true);
        } finally {
          setDancLoading(false);
        }
      };

      loadExistingDANCData();
    }, [
      orgId,
      appId,
      title,
      sectionId,
      dataLoaded,
      applicationStage,
      handleAdequacyCheck,
      handleAdequacyRemarks,
    ]);

    // Clear messages after timeout
    useEffect(() => {
      if (dancError || dancSuccess) {
        const timer = setTimeout(() => {
          setDancError("");
          setDancSuccess("");
        }, 5000);
        return () => clearTimeout(timer);
      }
    }, [dancError, dancSuccess]);

    const adequacy = adequacyChecks[sectionId] || { status: "", remarks: "" };

    // Fetch scrutiny remarks when status is Inadequate
    useEffect(() => {
      const fetchScrutinyRemarks = async () => {
        if (adequacy.status === "Inadequate") {
          try {
            setLoadingRemarks(true);
            const type = "Document Review";
            console.log(
              `🔍 Fetching scrutiny remarks for sectionName="${title}", type="${type}"`,
            );

            const response = await getScrutinyRemarks({
              sectionName: title,
              type,
            });

            console.log("📄 Scrutiny remarks response:", response.data);

            if (response.data.success) {
              const remarks = (response.data.data || []).map((r) => ({
                ...r,
                type,
              }));
              console.log(
                `✅ Found ${remarks.length} scrutiny remarks for "${title}"`,
              );
              setScrutinyRemarks(remarks);
            } else {
              console.log(`❌ No scrutiny remarks found for "${title}"`);
              setScrutinyRemarks([]);
            }
          } catch (error) {
            console.error("Error fetching scrutiny remarks:", error);
            setScrutinyRemarks([]);
          } finally {
            setLoadingRemarks(false);
          }
        } else {
          setScrutinyRemarks([]);
        }
      };

      fetchScrutinyRemarks();
    }, [adequacy.status, title]);

    if (
      !["15", "15a", "15b"].includes(applicationStage) ||
      currentUserTypeId !== "4"
    )
      return null;

    const isAdequacyReadOnly = applicationStage === "15a";

    const handleStatusChange = (status) => {
      handleAdequacyCheck(sectionId, status);
    };

    const handleRemarksChange = (remarks) => {
      handleAdequacyRemarks(sectionId, remarks);
    };

    const handleCheckboxChange = (option, checked) => {
      let newSelectedOptions;
      if (checked) {
        newSelectedOptions = [...selectedRemarksOptions, option];
      } else {
        newSelectedOptions = selectedRemarksOptions.filter(
          (item) => item.inadequate_remarks !== option.inadequate_remarks,
        );
      }
      setSelectedRemarksOptions(newSelectedOptions);

      const remarksText = newSelectedOptions
        .map((item) =>
          typeof item === "string" ? item : item.inadequate_remarks,
        )
        .join(", ");
      handleRemarksChange(remarksText);
    };

    const handleClearAllCheckboxes = () => {
      setSelectedRemarksOptions([]);
      handleRemarksChange("");
    };

    const handleSubmit = async () => {
      try {
        setDancLoading(true);
        setDancError("");
        setDancSuccess("");

        await handleAdequacySubmit(sectionId);

        if (adequacy.status) {
          if (existingDANCRecord) {
            const updateData = {
              subsectionName: title,
              informationStatus: adequacy.status,
              observation:
                adequacy.status === "Inadequate"
                  ? adequacy.remarks || "Document adequacy marked as Inadequate"
                  : "Document adequacy check completed",
              NCtype: expectedNCType,
              ClauseNo: null,
              isactive: 1,
              updatedby: localStorage.getItem("orgId") || 0,
            };

            const recordId = existingDANCRecord.Id || 0;
            const response = await updateDANCBySourceIdNewAppType(
              orgId,
              appId,
              "Change In CAB Premises",
              recordId,
              0,
              0,
              updateData,
            );

            if (response.data.success) {
              setExistingDANCRecord(response.data.data);
              setDancSuccess("Record updated successfully");
              if (handleAdequacySubmitted) {
                handleAdequacySubmitted(sectionId, true);
              }
            } else {
              throw new Error(response.data.message || "Update failed");
            }
          } else {
            const dancData = {
              Orgid: parseInt(orgId),
              Appid: parseInt(appId),
              SectionName: "Change In CAB Premises",
              SubsectionName: title,
              InformationProvided: adequacy.remarks || "",
              InformationStatus: adequacy.status,
              Observation:
                adequacy.status === "Inadequate"
                  ? adequacy.remarks || "Document adequacy marked as Inadequate"
                  : "Document adequacy check completed",
              assessorId: localStorage.getItem("orgId") || 0,
              Insertedby: localStorage.getItem("orgId") || 0,
              NCtype: expectedNCType,
              ClauseNo: null,
              assessmentId: 0,
            };

            const response = await insertDANC(dancData);
            if (response.data.success) {
              setExistingDANCRecord(response.data.data);
              setDancSuccess("Record created successfully");
              if (handleAdequacySubmitted) {
                handleAdequacySubmitted(sectionId, true);
              }
            } else {
              throw new Error(response.data.message || "Insert failed");
            }
          }
        }
      } catch (error) {
        console.error("Error submitting adequacy check:", error);
        setDancError("Error: " + error.message);
      } finally {
        setDancLoading(false);
      }
    };

    return (
      <Box
        sx={{
          mb: 3,
          p: 3,
          backgroundColor: "#f8f9fa",
          borderRadius: 2,
          border: "2px solid #e9ecef",
          borderLeft: "4px solid #007bff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "#495057",
              fontWeight: 600,
              fontSize: "1.1rem",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            📋 Adequacy Check - {title}
            {isAdequacyReadOnly && (
              <Chip
                label="🔒 Read Only"
                size="small"
                sx={{
                  bgcolor: "#f5f5f5",
                  color: "#757575",
                  fontWeight: "bold",
                  fontSize: "0.7rem",
                }}
              />
            )}
          </Typography>

          {adequacy.status && (
            <Chip
              label={`${adequacy.status}`}
              size="small"
              sx={{
                bgcolor: adequacy.status === "Adequate" ? "#28a745" : "#dc3545",
                color: "white",
                fontWeight: "bold",
              }}
            />
          )}
        </Box>

        {dancError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setDancError("")}
          >
            {dancError}
          </Alert>
        )}
        {dancSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setDancSuccess("")}
          >
            {dancSuccess}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Document Status</InputLabel>
              <Select
                value={adequacy.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                label="Document Status"
                disabled={isAdequacyReadOnly}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="">-- Select Status --</MenuItem>
                <MenuItem value="Adequate">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon
                      sx={{ color: "#28a745", fontSize: "1rem" }}
                    />
                    Adequate
                  </Box>
                </MenuItem>
                <MenuItem value="Inadequate">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ErrorIcon sx={{ color: "#dc3545", fontSize: "1rem" }} />
                    Inadequate
                  </Box>
                </MenuItem>
                {currentUserTypeId === "4" && (
                  <MenuItem value="To be Verify">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VerifyIcon
                        sx={{ color: "#35dcdcff", fontSize: "1rem" }}
                      />
                      To be Verify
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          {adequacy.status === "Inadequate" && (
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={
                  isAdequacyReadOnly
                    ? "Remarks (View Only)"
                    : "Remarks (Required)"
                }
                placeholder={
                  isAdequacyReadOnly
                    ? "View Only"
                    : "Type your remarks here. Use dropdown below for suggestions."
                }
                value={adequacy.remarks || ""}
                onChange={(e) => handleRemarksChange(e.target.value)}
                required={!isAdequacyReadOnly}
                variant="outlined"
                disabled={isAdequacyReadOnly}
                sx={{
                  "& .MuiOutlinedInput-root": { backgroundColor: "white" },
                }}
              />

              {!isAdequacyReadOnly && scrutinyRemarks.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      setIsCheckboxDropdownOpen(!isCheckboxDropdownOpen)
                    }
                    sx={{ mb: 1, borderColor: "#007bff", color: "#007bff" }}
                  >
                    {isCheckboxDropdownOpen ? "Hide" : "Show"} Predefined
                    Remarks ({scrutinyRemarks.length} available)
                  </Button>

                  {selectedRemarksOptions.length > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleClearAllCheckboxes}
                      sx={{ ml: 1, color: "#dc3545" }}
                    >
                      Clear All ({selectedRemarksOptions.length})
                    </Button>
                  )}

                  {isCheckboxDropdownOpen && (
                    <Paper
                      elevation={3}
                      sx={{
                        mt: 1,
                        maxHeight: 300,
                        overflow: "auto",
                        borderRadius: 2,
                      }}
                    >
                      <MenuList dense>
                        {scrutinyRemarks.map((option, index) => {
                          const remarkText =
                            typeof option === "string"
                              ? option
                              : option.inadequate_remarks;
                          const isSelected = selectedRemarksOptions.some(
                            (item) =>
                              (typeof item === "string"
                                ? item
                                : item.inadequate_remarks) === remarkText,
                          );

                          return (
                            <MenuItem
                              key={index}
                              onClick={() =>
                                handleCheckboxChange(option, !isSelected)
                              }
                              sx={{ py: 1, px: 2 }}
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{ p: 0, mr: 2 }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.875rem" }}
                              >
                                {remarkText}
                              </Typography>
                            </MenuItem>
                          );
                        })}
                      </MenuList>
                    </Paper>
                  )}
                </Box>
              )}

              <Typography
                variant="caption"
                sx={{
                  color: "#6c757d",
                  display: "block",
                  mt: 1,
                  fontStyle: "italic",
                }}
              >
                {loadingRemarks
                  ? "Loading remarks..."
                  : scrutinyRemarks.length > 0
                    ? `${scrutinyRemarks.length} predefined remark(s) available.`
                    : ""}
              </Typography>
            </Grid>
          )}
        </Grid>

        {adequacy.status && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSubmit}
              disabled={
                isAdequacyReadOnly ||
                !adequacy.status ||
                (adequacy.status === "Inadequate" &&
                  !adequacy.remarks?.trim()) ||
                loading ||
                dancLoading
              }
              sx={{
                background: existingDANCRecord
                  ? "linear-gradient(135deg, #2196f3 0%, #1976d2 100%)"
                  : "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                color: "white",
                fontWeight: 600,
                px: 3,
                py: 1,
              }}
            >
              {loading || dancLoading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "white" }} />
                  {existingDANCRecord ? "Updating..." : "Submitting..."}
                </Box>
              ) : existingDANCRecord ? (
                "🔄 Update"
              ) : (
                "📤 Submit"
              )}
            </Button>
          </Box>
        )}
      </Box>
    );
  },
);

const ChangeINCabPermises = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL (only orgId)
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedAppId = searchParams.get("appId");
  const encryptedLabTypeId = searchParams.get("labTypeId");

  // Decrypt parameters
  const decryptedOrgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const decryptedAppId = encryptedAppId ? decryptText(encryptedAppId) : null;
  const decryptedLabTypeId = encryptedLabTypeId
    ? decryptText(encryptedLabTypeId)
    : null;

  const [orgId, setOrgId] = useState(decryptedOrgId);
  const [appId, setAppId] = useState(decryptedAppId);
  const [labTypeId, setLabTypeId] = useState(decryptedLabTypeId);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState("");
  const [userTypeId, setUserTypeId] = useState(
    localStorage.getItem("userTypeId") || "",
  );

  // Basic Details State (read-only)
  const [cabName, setCabName] = useState("");
  const [cabType, setCabType] = useState("0");
  const [country, setCountry] = useState("101"); // Default to India
  const [state, setState] = useState("0");
  const [city, setCity] = useState("0");
  const [address, setAddress] = useState("");
  const [district, setDistrict] = useState("");
  const [pincode, setPincode] = useState("");
  const [mobile, setMobile] = useState("");
  const [landlineCode, setLandlineCode] = useState("");
  const [landline, setLandline] = useState("");
  const [faxCode, setFaxCode] = useState("");
  const [fax, setFax] = useState("");
  const [email, setEmail] = useState("");

  // Shifting Location
  const [shiftingLocation, setShiftingLocation] = useState("");

  // New Location Details State
  const [newAddress, setNewAddress] = useState("");
  const [newGstin, setNewGstin] = useState("");
  const [newCity, setNewCity] = useState("0");
  const [newDistrict, setNewDistrict] = useState("");
  const [newPincode, setNewPincode] = useState("");
  const [newCountryCode, setNewCountryCode] = useState("");
  const [newContact, setNewContact] = useState("");
  const [newFaxCode, setNewFaxCode] = useState("");
  const [newFaxNo, setNewFaxNo] = useState("");
  const [newEmail, setNewEmail] = useState("");

  // Legal Identity State - New Structure
  const [legalEntityType, setLegalEntityType] = useState("");
  const [legalIdentityName, setLegalIdentityName] = useState("");

  // Legal Entity Type Specific Fields
  const [ownerName, setOwnerName] = useState("");
  const [mciRegNumber, setMciRegNumber] = useState("");
  const [stateRegistrationNumber, setStateRegistrationNumber] = useState("");
  const [partnershipOwnerName, setPartnershipOwnerName] = useState("");
  const [legalIdentity_Address, setLegalIdentity_Address] = useState("");
  const [legalIdentity_State, setLegalIdentity_State] = useState("");
  const [legalIdentity_Pincode, setLegalIdentity_Pincode] = useState("");
  const [governmentIsAutonomous, setGovernmentIsAutonomous] = useState("");
  const [societyIsAutonomous, setSocietyIsAutonomous] = useState("");
  const [panTanNumber, setPanTanNumber] = useState("");
  const [partnershipLegalEntityName, setPartnershipLegalEntityName] =
    useState("");
  const [
    partnershipStateRegistrationNumber,
    setPartnershipStateRegistrationNumber,
  ] = useState("");
  const [companiesActPincode, setCompaniesActPincode] = useState("");

  // File Upload States - New Structure
  const [legalIdentityDoc1, setLegalIdentityDoc1] = useState(null); // MCI/NMC Certificate
  const [legalIdentityDoc2, setLegalIdentityDoc2] = useState(null); // State Registration Certificate
  const [partnershipDeedFile, setPartnershipDeedFile] = useState(null);
  const [llpCertificateFile, setLlpCertificateFile] = useState(null);
  const [companiesActCertificateFile, setCompaniesActCertificateFile] =
    useState(null);
  const [publicLtdCertificateFile, setPublicLtdCertificateFile] =
    useState(null);
  const [governmentCertificateFile, setGovernmentCertificateFile] =
    useState(null);
  const [trustCertificateFile, setTrustCertificateFile] = useState(null);
  const [societyCertificateFile, setSocietyCertificateFile] = useState(null);
  const [opcCertificateFile, setOpcCertificateFile] = useState(null);
  const [managementFile, setManagementFile] = useState(null);
  const [declarationFile, setDeclarationFile] = useState(null);
  const [remarksFile, setRemarksFile] = useState(null);
  const [gstExemptionFile, setGstExemptionFile] = useState(null);
  const [otherFile, setOtherFile] = useState(null);

  // Validation Errors
  const [validationErrors, setValidationErrors] = useState({});

  // Adequacy Checks State (unified pattern)
  const [adequacyChecks, setAdequacyChecks] = useState({});
  const [adequacySubmitted, setAdequacySubmitted] = useState({});

  // Other States
  const [noClaim, setNoClaim] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [showDeclarationUpload, setShowDeclarationUpload] = useState(false);
  const [isChangeLegalEntity, setIsChangeLegalEntity] = useState("");

  // Dropdown Options
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [newCities, setNewCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [newDistricts, setNewDistricts] = useState([]);
  const [districtName, setDistrictName] = useState("");

  // Dialog States
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [password, setPassword] = useState("");

  // Payment Status States
  const [showSubmitButton, setShowSubmitButton] = useState(false);
  const [showPaymentButton, setShowPaymentButton] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null, 'failed', 'success', 'pending'
  const [hasDataInDB, setHasDataInDB] = useState(false); // Track if data is saved in database

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Check payment status to determine Final Submit button visibility
  const checkPaymentStatus = async () => {
    try {
      if (!orgId || !appId) {
        console.log('⏭️ Skipping payment check - missing orgId or appId');
        return;
      }
      const response = await paymentService.checkPaymentStatus(orgId, appId);
      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const hasSuccessfulPayment = response.data.some(payment => {
          const hasOrderStatus = payment.order_status?.toLowerCase() === 'success' || payment.order_status === 'Success';
          return hasOrderStatus;
        });
        const hasFailedPayment = response.data.some(payment =>
          payment.order_status?.toLowerCase() === 'failed'
        );
        if (hasSuccessfulPayment) {
          setPaymentStatus('success');
          setShowPaymentButton(false);
          const isStageValid = stage !== "15a" && stage !== "15" && stage !== "15b" && stage !== "15c";
          const showLocationPanel = shiftingLocation === "Same District";
          if ((shiftingLocation === "Another District" || (showLocationPanel && isStageValid))) {
            setShowSubmitButton(true);
          } else {
            setShowSubmitButton(false);
          }
        } else if (hasFailedPayment) {
          setPaymentStatus('failed');
          setShowSubmitButton(false);
          const isStageValid = stage !== "15a" && stage !== "15" && stage !== "15b" && stage !== "15c";
          const showLocationPanel = shiftingLocation === "Same District";
          
          // Check if all mandatory fields are filled AND data is saved in database
          let isDataFilled = false;
          if (showLocationPanel && hasDataInDB) {
            isDataFilled = 
              newAddress && newAddress.trim() !== "" &&
              noClaim &&
              isChangeLegalEntity &&
              managementFile &&
              // If changing legal entity, check those fields too
              (isChangeLegalEntity === "No" || 
                (legalEntityType && legalIdentityName && legalIdentityName.trim() !== ""));
          }
          
          setShowPaymentButton(isStageValid && isDataFilled);
        } else {
          setPaymentStatus('pending');
          setShowSubmitButton(false);
          setShowPaymentButton(false);
        }
      } else {
        setPaymentStatus(null);
        setShowSubmitButton(false);
        const isReadOnlyStage = stage === "15" || stage === "15a" || stage === "15b" || stage === "15c";
        const showLocationPanel = shiftingLocation === "Same District";
        
        // Check if all mandatory fields are filled AND data is saved in database
        let isDataFilled = false;
        if (showLocationPanel && hasDataInDB) {
          isDataFilled = 
            newAddress && newAddress.trim() !== "" &&
            noClaim &&
            isChangeLegalEntity &&
            managementFile &&
            // If changing legal entity, check those fields too
            (isChangeLegalEntity === "No" || 
              (legalEntityType && legalIdentityName && legalIdentityName.trim() !== ""));
        }
        
        const shouldShowPayment = !isReadOnlyStage && isDataFilled;
        setShowPaymentButton(shouldShowPayment);
      }
    } catch (error) {
      setPaymentStatus(null);
      setShowSubmitButton(false);
      const isReadOnlyStage = stage === "15" || stage === "15a" || stage === "15b" || stage === "15c";
      const showLocationPanel = shiftingLocation === "Same District";
      
      // Check if all mandatory fields are filled AND data is saved in database
      let isDataFilled = false;
      if (showLocationPanel && hasDataInDB) {
        isDataFilled = 
          newAddress && newAddress.trim() !== "" &&
          noClaim &&
          isChangeLegalEntity &&
          managementFile &&
          // If changing legal entity, check those fields too
          (isChangeLegalEntity === "No" || 
            (legalEntityType && legalIdentityName && legalIdentityName.trim() !== ""));
      }
      
      setShowPaymentButton(!isReadOnlyStage && isDataFilled);
    }
  };

  // Unified Adequacy Check Handlers
  const handleAdequacyCheck = useCallback((sectionId, value) => {
    setAdequacyChecks((prev) => {
      const current = prev[sectionId] || {};
      return {
        ...prev,
        [sectionId]: {
          ...current,
          status: value,
          remarks: value === "Adequate" ? "" : current.remarks || "",
        },
      };
    });
  }, []);

  const handleAdequacyRemarks = useCallback((sectionId, remarks) => {
    setAdequacyChecks((prev) => {
      const current = prev[sectionId] || {};
      if (current.remarks === remarks) {
        return prev;
      }
      return {
        ...prev,
        [sectionId]: {
          ...current,
          remarks: remarks,
        },
      };
    });
  }, []);

  const handleAdequacySubmit = useCallback(async (sectionId) => {
    console.log(`Submitting adequacy check for section: ${sectionId}`);
    // The actual DANC API calls are handled within AdequacyCheckSection component
    return Promise.resolve();
  }, []);

  const handleAdequacySubmitted = useCallback((sectionId, isSubmitted) => {
    setAdequacySubmitted((prev) => ({
      ...prev,
      [sectionId]: isSubmitted,
    }));
  }, []);

  // Load data on mount
  useEffect(() => {
    if (orgId) {
      initializePage();
    }
  }, [orgId]);

  // Reset district name when state or district changes
  useEffect(() => {
    setDistrictName("");
  }, [state, district]);

  // Load district name when states and district are available
  useEffect(() => {
    const loadDistrictName = async () => {
      if (states.length > 0 && state && state !== "0" && district) {
        // Find the state object to get State_Code
        const stateObj = states.find(
          (s) => s.id?.toString() === state?.toString(),
        );
        if (!stateObj || !stateObj.State_Code) {
          console.log("State_Code not found for state id:", state);
          return;
        }

        const stateCode = stateObj.State_Code;
        const districtsList = await loadDistricts(stateCode);

        // Try matching by District_Code first (API returns District_Code)
        let districtObj = districtsList.find(
          (d) =>
            d.District_Code === parseInt(district) ||
            d.District_Code === district,
        );

        // Fallback to id field match
        if (!districtObj) {
          districtObj = districtsList.find(
            (d) => d.id === parseInt(district) || d.id === district,
          );
        }

        // Fallback to exact name match (case-insensitive)
        if (!districtObj && typeof district === "string") {
          districtObj = districtsList.find(
            (d) =>
              d.District_Name_Eng?.toLowerCase().trim() ===
              district.toLowerCase().trim(),
          );
        }

        // Fallback to partial name match
        if (!districtObj && typeof district === "string") {
          districtObj = districtsList.find(
            (d) =>
              d.District_Name_Eng?.toLowerCase().includes(
                district.toLowerCase(),
              ) ||
              district
                .toLowerCase()
                .includes(d.District_Name_Eng?.toLowerCase()),
          );
        }

        if (districtObj) {
          console.log("✓ Found district:", districtObj.District_Name_Eng);
          setDistrictName(districtObj.District_Name_Eng);
        } else {
          console.log("✗ No district match found for:", district);
        }
      } else {
        console.log("Conditions not met:", {
          hasStates: states.length > 0,
          hasState: state && state !== "0",
          hasDistrict: !!district,
          hasNoDistrictName: !districtName,
        });
      }
    };
    loadDistrictName();
  }, [states, state, district]);

  const initializePage = async () => {
    try {
      setLoading(true);
      // Load states first
      await loadStates(101); // Load India's states

      // Load basic details
      await loadBasicDetails(); // This loads the state and cities for main lab

      // Now load functions that depend on appId
      if (appId) {
        await Promise.all([loadStage(), loadLocationDetails()]);
      }
    } catch (err) {
      console.error("Error initializing page:", err);
      showSnackbar("Failed to load application data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Re-check payment status when shiftingLocation or stage changes
  useEffect(() => {
    if (orgId && appId) {
      checkPaymentStatus();
    }
  }, [shiftingLocation, stage]);

  // Re-check payment status when form fields change
  useEffect(() => {
    if (orgId && appId) {
      checkPaymentStatus();
    }
  }, [newAddress, noClaim, isChangeLegalEntity, legalEntityType, legalIdentityName, managementFile, hasDataInDB]);

  const loadStates = async (countryId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/states`,
      );
      if (response.data.success) {
        setStates(response.data.data || []);
      }
    } catch (err) {
      console.error("Error loading states:", err);
    }
  };

  const loadCities = async (stateId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/cities/${stateId}`,
      );
      if (response.data.success) {
        setCities(response.data.data || []);
        setNewCities(response.data.data || []);
      }
    } catch (err) {
      console.error("Error loading cities:", err);
    }
  };

  const loadDistricts = async (stateCode) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/districts/${stateCode}`,
      );
      if (response.data.success) {
        const districtsData = response.data.data || [];
        setDistricts(districtsData);
        setNewDistricts(districtsData);
        return districtsData;
      }
      return [];
    } catch (err) {
      console.error("Error loading districts:", err);
      return [];
    }
  };

  const loadStage = async () => {
    try {
      if (!appId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/application-stage?appid=${appId}`,
      );
      if (response.data.success) {
        setStage(response.data.data.stage || "");
      }
    } catch (err) {
      console.error("Error loading stage:", err);
    }
  };

  const checkDeclarationFile = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/change-premises/check-declaration/${appId}`,
      );
      if (response.data.success && response.data.data) {
        setShowDeclarationUpload(true);
      }
    } catch (err) {
      console.error("Error checking declaration:", err);
    }
  };

  const loadBasicDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/change-name-app/main-lab-details/${orgId}`,
      );
      if (response.data.success) {
        const data = response.data.data;

        setCabName(data.CabName || "");

        // Set CabType directly (Diagnostic, Calibration, Medical)
        setCabType(data.CabType || "0");

        setCountry(
          data.CountryId?.toString() || data.Country?.toString() || "101",
        );
        setState(data.stateId?.toString() || data.State?.toString() || "0");
        setCity(data.cityId?.toString() || data.City?.toString() || "0");

        setAddress(data.Address || "");
        setDistrict(data.District?.toString() || "");
        setPincode(data.Pincode || "");
        setMobile(data.MobileNo || "");
        setEmail(data.Email || "");

        // Parse Landline if it has format like "Code-Number"
        if (data.LandlineNo) {
          const landlineParts = data.LandlineNo.split("-");
          if (landlineParts.length === 2) {
            setLandlineCode(landlineParts[0]);
            setLandline(landlineParts[1]);
          } else {
            setLandline(data.LandlineNo);
          }
        }

        // Parse Fax if it has format like "Code-Number"
        if (data.FaxNo) {
          const faxParts = data.FaxNo.split("-");
          if (faxParts.length === 2) {
            setFaxCode(faxParts[0]);
            setFax(faxParts[1]);
          } else {
            setFax(data.FaxNo);
          }
        }

        // Load dependent dropdowns
        if (data.stateId || data.State) {
          await loadCities(data.stateId || data.State);

          // Load districts for the state
          const stateObj = states.find(
            (s) =>
              s.id?.toString() === (data.stateId || data.State)?.toString(),
          );
          if (stateObj && stateObj.State_Code) {
            await loadDistricts(stateObj.State_Code);
          }
        }
      }
    } catch (err) {
      console.error("Error loading basic details:", err);
    }
  };

  const loadLocationDetails = async () => {
    try {
      if (!appId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/cab-permises/${appId}/${orgId}`,
      );
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setHasDataInDB(true); // Mark that data exists in database

        // Basic location fields - handle both uppercase and lowercase variations
        setShiftingLocation(
          data.ShiftingOfLocation || data.shiftingOfLocation || "",
        );
        setNewAddress(data.Address || data.address || data.newAddress || "");
        setNewGstin(data.GSTIN || data.gstin || "");
        setNewCity((data.City || data.city || data.newCity)?.toString() || "0");
        setNewDistrict(
          data.District || data.district || data.newDistrict || "",
        );
        setNewPincode(data.PinCode || data.pincode || data.newPinCode || "");
        setNewEmail(data.Email || data.email || data.newEmailId || "");

        // Parse contact - handle both field name variations
        // const contactData =
        //   data.ContactNo || data.contactNo || data.newContactNo;
        // if (contactData && contactData !== "-") {
        //   const parts = contactData.split("~");
        //   setNewCountryCode(parts[0] || "");
        //   setNewContact(parts[1] || "");
        // }

        // // Parse fax - handle both field name variations
        // const faxData = data.Fax || data.fax || data.newFaxNo;
        // if (faxData && faxData !== "-") {
        //   const parts = faxData.split("~");
        //   setNewFaxCode(parts[0] || "");
        //   setNewFaxNo(parts[1] || "");
        // }

        setNewContact(data.ContactNo || data.contactNo || data.newContactNo || "");
        setNewFaxNo(data.Fax || data.fax || data.newFaxNo || "");

        // Set isChangeLegalEntity from changeLegalEntity field
        if (data.changeLegalEntity) {
          setIsChangeLegalEntity(data.changeLegalEntity);
        } else if (data.legalEntityType || data.LegalIdentityType) {
          setIsChangeLegalEntity("Yes");
        } else {
          setIsChangeLegalEntity("");
        }

        // Set legal entity type (check both field names)
        const entityType = data.legalEntityType || data.LegalIdentityType;
        if (entityType) {
          setLegalEntityType(entityType);
        }

        // Set entity-specific fields
        setLegalIdentityName(
          data.legalEntityName ||
            data.LegalIdentityName ||
            data.legalIdentityName ||
            "",
        );
        setOwnerName(data.ownerName || "");
        setMciRegNumber(data.mciRegNumber || "");
        setStateRegistrationNumber(data.stateRegistrationNumber || "");
        setPanTanNumber(data.panTanNumber || "");
        setPartnershipLegalEntityName(data.partnershipLegalEntityName || "");
        setPartnershipOwnerName(data.partnershipOwnerName || "");
        setPartnershipStateRegistrationNumber(
          data.partnershipStateRegistrationNumber || "",
        );
        setLegalIdentity_Address(data.legalIdentity_Address || "");
        setLegalIdentity_State(data.legalIdentity_State || "");
        setLegalIdentity_Pincode(data.legalIdentity_Pincode || "");
        setCompaniesActPincode(data.companiesActPincode || "");
        setGovernmentIsAutonomous(data.governmentIsAutonomous || "");
        setSocietyIsAutonomous(data.societyIsAutonomous || "");

        // Set file references - handle both uppercase and lowercase field names
        if (data.legalIdentityDoc1)
          setLegalIdentityDoc1(createFileObject(data.legalIdentityDoc1));
        if (data.legalIdentityDoc2)
          setLegalIdentityDoc2(createFileObject(data.legalIdentityDoc2));
        if (data.gstExemptionFile)
          setGstExemptionFile(createFileObject(data.gstExemptionFile));
        if (data.partnershipDeedFile)
          setPartnershipDeedFile(createFileObject(data.partnershipDeedFile));
        if (data.llpCertificateFile)
          setLlpCertificateFile(createFileObject(data.llpCertificateFile));
        if (data.companiesActCertificateFile)
          setCompaniesActCertificateFile(
            createFileObject(data.companiesActCertificateFile),
          );
        if (data.publicLtdCertificateFile)
          setPublicLtdCertificateFile(
            createFileObject(data.publicLtdCertificateFile),
          );
        if (data.governmentCertificateFile)
          setGovernmentCertificateFile(
            createFileObject(data.governmentCertificateFile),
          );
        if (data.trustCertificateFile)
          setTrustCertificateFile(createFileObject(data.trustCertificateFile));
        if (data.societyCertificateFile)
          setSocietyCertificateFile(
            createFileObject(data.societyCertificateFile),
          );
        if (data.OPCCertificate || data.opcCertificateFile)
          setOpcCertificateFile(
            createFileObject(data.OPCCertificate || data.opcCertificateFile),
          );
        if (data.otherFile) setOtherFile(createFileObject(data.otherFile));

        // Management and declaration files
        if (data.ManagementSystemDocument || data.managementSystemDocument)
          setManagementFile(
            createFileObject(
              data.ManagementSystemDocument || data.managementSystemDocument,
            ),
          );
        if (
          data.DeclerationFile ||
          data.declerationFile ||
          data.declarationFile
        )
          setDeclarationFile(
            createFileObject(
              data.DeclerationFile ||
                data.declerationFile ||
                data.declarationFile,
            ),
          );
        if (data.fileUploadWithRemarks)
          setRemarksFile(createFileObject(data.fileUploadWithRemarks));

        setRemarks(data.remarksByCab || "");
        setNoClaim(data.is_DeclaredByCab === 1 || data.isDeclaredByCab === 1);
      } else {
        setHasDataInDB(false); // No data in database
      }
    } catch (err) {
      console.error("Error loading location details:", err);
      setHasDataInDB(false); // Error means no data
    }
  };

  const createFileObject = (fileName) => {
    if (!fileName) return null;
    return {
      fileName: fileName,
      key: `live/${fileName}`, // Just the filename, backend will add live/ prefix
      originalName: fileName,
    };
  };

  const handleCountryChange = async (value) => {
    // Country is fixed to India, no change allowed
    setCountry("101");
    setState("0");
    setCity("0");
  };

  const handleStateChange = async (value) => {
    setState(value);
    setCity("0");
    setNewCity("0");
    if (value !== "0") {
      await loadCities(value);
    }
  };

  const handleShiftingLocationChange = (value) => {
    setShiftingLocation(value);
    if (value === "Another District") {
      //setShiftingLocation('')
      alert(
        "If the accredited CAB shifts its location to another city, the existing CAB accreditation status will be withdrawn and CAB file will be closed. If CAB wishes accreditation in new city, they have to apply afresh with relevant application fees. The application will be treated as new application and new CAB ID will be given. Kindly coordinate with your Dealing Officer.",
      );
    }
  };

  const handleLegalEntityChange = (field, value) => {
    if (field === "legalEntityType") {
      setLegalEntityType(value);
      // Clear all entity-specific fields when changing type
      setOwnerName("");
      setMciRegNumber("");
      setStateRegistrationNumber("");
      setPanTanNumber("");
      setPartnershipLegalEntityName("");
      setPartnershipOwnerName("");
      setPartnershipStateRegistrationNumber("");
      setLegalIdentity_Address("");
      setLegalIdentity_State("");
      setLegalIdentity_Pincode("");
      setCompaniesActPincode("");
      setGovernmentIsAutonomous("");
      setSocietyIsAutonomous("");
      // Clear all files
      setLegalIdentityDoc1(null);
      setLegalIdentityDoc2(null);
      setGstExemptionFile(null);
      setPartnershipDeedFile(null);
      setLlpCertificateFile(null);
      setCompaniesActCertificateFile(null);
      setPublicLtdCertificateFile(null);
      setGovernmentCertificateFile(null);
      setTrustCertificateFile(null);
      setSocietyCertificateFile(null);
      setOpcCertificateFile(null);
      setOtherFile(null);
      // Clear validation errors
      setValidationErrors({});
    }
  };

  const handleIsChangeLegalEntityChange = (value) => {
    setIsChangeLegalEntity(value);
    if (value === "No") {
      // Clear all legal entity related fields when user selects "No"
      setLegalIdentityName("");
      setLegalEntityType("");
      setOwnerName("");
      setMciRegNumber("");
      setStateRegistrationNumber("");
      setPanTanNumber("");
      setPartnershipLegalEntityName("");
      setPartnershipOwnerName("");
      setPartnershipStateRegistrationNumber("");
      setLegalIdentity_Address("");
      setLegalIdentity_State("");
      setLegalIdentity_Pincode("");
      setCompaniesActPincode("");
      setGovernmentIsAutonomous("");
      setSocietyIsAutonomous("");
      // Clear all files
      setLegalIdentityDoc1(null);
      setLegalIdentityDoc2(null);
      setGstExemptionFile(null);
      setPartnershipDeedFile(null);
      setLlpCertificateFile(null);
      setCompaniesActCertificateFile(null);
      setPublicLtdCertificateFile(null);
      setGovernmentCertificateFile(null);
      setTrustCertificateFile(null);
      setSocietyCertificateFile(null);
      setOpcCertificateFile(null);
      setOtherFile(null);
      // Clear validation errors
      setValidationErrors({});
    }
  };

  const handleNumericChange = (field, value, maxLength) => {
    const numericValue = value.replace(/\D/g, "").slice(0, maxLength);
    if (field === "legalIdentity_Pincode") {
      setLegalIdentity_Pincode(numericValue);
    } else if (field === "companiesActPincode") {
      setCompaniesActPincode(numericValue);
    }
  };

  const requiresRegistrationValidation = (entityType) => {
    return entityType === "Proprietorship" || entityType === "Partnership";
  };

  // Helper function to extract filename from file object
  const extractFileName = (fileData) => {
    if (!fileData) return "";
    if (typeof fileData === "string") return fileData;
    if (typeof fileData === "object") {
      return fileData.fileName || fileData.imgName || fileData.key || "";
    }
    return "";
  };

  const handleS3FileUpload = (fieldName, fileUrl) => {
    // Map field names to state setters
    const fileSetters = {
      legalIdentityDoc1: setLegalIdentityDoc1,
      legalIdentityDoc2: setLegalIdentityDoc2,
      gstExemptionFile: setGstExemptionFile,
      partnershipDeedFile: setPartnershipDeedFile,
      llpCertificateFile: setLlpCertificateFile,
      companiesActCertificateFile: setCompaniesActCertificateFile,
      publicLtdCertificateFile: setPublicLtdCertificateFile,
      governmentCertificateFile: setGovernmentCertificateFile,
      trustCertificateFile: setTrustCertificateFile,
      societyCertificateFile: setSocietyCertificateFile,
      opcCertificateFile: setOpcCertificateFile,
      otherFile: setOtherFile,
      managementFile: setManagementFile,
      declarationFile: setDeclarationFile,
      remarksFile: setRemarksFile,
    };

    if (fileSetters[fieldName]) {
      fileSetters[fieldName](fileUrl ? { fileName: fileUrl } : null);
    }
  };

  const handleSaveDetails = async () => {
    try {
      // Validation
      if (!newAddress.trim()) {
        showSnackbar("Please enter new address", "warning");
        return false;
      }

      if (!noClaim) {
        showSnackbar("Please check the No Claim checkbox", "warning");
        return false;
      }

      // Validate legal entity question
      if (!isChangeLegalEntity) {
        showSnackbar(
          "Please select if this is a change in legal entity",
          "warning",
        );
        return false;
      }

      // Only validate legal entity details if user selected 'Yes'
      if (isChangeLegalEntity === "Yes") {
        if (!legalEntityType) {
          showSnackbar("Please select a legal entity type", "warning");
          return false;
        }

        if (!legalIdentityName.trim()) {
          showSnackbar("Please enter legal identity name", "warning");
          return false;
        }
      }

      if (!managementFile) {
        showSnackbar("Please upload Management System Document", "warning");
        return false;
      }

      if (showDeclarationUpload && !declarationFile) {
        showSnackbar("Please upload Declaration file", "warning");
        return false;
      }

      if (remarks && !remarksFile) {
        showSnackbar("Please upload file along with remarks", "warning");
        return false;
      }

      const formData = {
        appid: appId,
        orgid: orgId,
        labtypeid: labTypeId,
        shiftingOfLocation: shiftingLocation,
        address: newAddress,
        gstin: newGstin,
        city: newCity,
        district: newDistrict,
        pincode: newPincode,
        //contact: `${newCountryCode}~${newContact}`,
        //fax: `${newFaxCode}~${newFaxNo}`,
        contact: newContact ? newContact : "",
        fax: newFaxNo ? newFaxNo : "",
        email: newEmail,
        changeLegalEntity: isChangeLegalEntity,
        legalEntityType: legalEntityType,
        legalEntityName: legalIdentityName,
        // Entity-specific fields
        ownerName: ownerName || "",
        mciRegNumber: mciRegNumber || "",
        stateRegistrationNumber: stateRegistrationNumber || "",
        panTanNumber: panTanNumber || "",
        partnershipLegalEntityName: partnershipLegalEntityName || "",
        partnershipOwnerName: partnershipOwnerName || "",
        partnershipStateRegistrationNumber:
          partnershipStateRegistrationNumber || "",
        legalIdentity_Address: legalIdentity_Address || "",
        legalIdentity_State: legalIdentity_State || "",
        legalIdentity_Pincode: legalIdentity_Pincode || "",
        companiesActPincode: companiesActPincode || "",
        governmentIsAutonomous: governmentIsAutonomous || "",
        societyIsAutonomous: societyIsAutonomous || "",
        // File uploads - extract filename string only
        legalIdentityDoc1: extractFileName(legalIdentityDoc1),
        legalIdentityDoc2: extractFileName(legalIdentityDoc2),
        gstExemptionFile: extractFileName(gstExemptionFile),
        partnershipDeedFile: extractFileName(partnershipDeedFile),
        llpCertificateFile: extractFileName(llpCertificateFile),
        companiesActCertificateFile: extractFileName(
          companiesActCertificateFile,
        ),
        publicLtdCertificateFile: extractFileName(publicLtdCertificateFile),
        governmentCertificateFile: extractFileName(governmentCertificateFile),
        trustCertificateFile: extractFileName(trustCertificateFile),
        societyCertificateFile: extractFileName(societyCertificateFile),
        opcCertificateFile: extractFileName(opcCertificateFile),
        otherFile: extractFileName(otherFile),
        managementSystemDocument: extractFileName(managementFile),
        declarationFile: extractFileName(declarationFile),
        remarksByCab: remarks,
        fileUploadWithRemarks: extractFileName(remarksFile),
        isDeclaredByCab: noClaim ? 1 : 0,
        // System fields
        createdBy: localStorage.getItem("orgId") || null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/cab-permises`,
        formData,
      );

      if (response.data.success) {
        showSnackbar("Data saved successfully", "success");
        await loadLocationDetails();
        await checkPaymentStatus(); // Re-check payment status after save
        return true;
      } else {
        showSnackbar(response.data.message || "Failed to save data", "error");
        return false;
      }
    } catch (err) {
      console.error("Error saving:", err);
      showSnackbar("Error saving data", "error");
      return false;
    }
  };

  const handlePasswordSubmit = async () => {
    try {
      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: orgId,
          password: password,
        },
      );

      if (!passwordResponse.data.success) {
        showSnackbar("Invalid password", "error");
        return;
      }

      // Execute action based on dialog type
      if (dialogType === "final-submit") {
        await handleFinalSubmit();
      } else if (dialogType === "payment") {
        navigate(`/main/medical/payment?paymentType=${encryptText('2')}&appId=${encryptAppId(appId)}`);
      } else if (dialogType === "send-to-nabl") {
        await handleSendToNABL();
      } else if (dialogType === "send-to-cab") {
        await handleSendToCab();
      } else if (dialogType === "generate-form11") {
        await handleGenerateForm11();
      } else if (dialogType === "close-nc") {
        await handleCloseNC();
      } else if (dialogType === "generate-form-11") {
        await handleGenerateForm11();
      } else if (dialogType === "go-for-assessment") {
        await handleGoForAssessment();
      } else if (dialogType === "process-completed") {
        await handleProcessCompleted();
      }

      setShowPasswordDialog(false);
      setPassword("");
    } catch (err) {
      console.error("Error with password submit:", err);
      showSnackbar("Error processing request", "error");
    }
  };

  const handleFinalSubmit = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/final-submit`,
        {
          appId: appId,
          orgId: orgId,
        },
      );

      if (response.data.success) {
        showSnackbar("Application submitted successfully", "success");
         setTimeout(() => {
          navigate(`/main/preview-report?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&observation=${encryptText('False')}&autoDownload=true&fromSubmission=true`);
        }, 1500);
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        showSnackbar(response.data.message || "Failed to submit", "error");
      }
    } catch (err) {
      console.error("Error in final submit:", err);
      showSnackbar("Error submitting application", "error");
    }
  };

  const handleSendToNABL = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/send-to-nabl`,
        {
          appId: appId,
          orgId: orgId,
        },
      );

      if (response.data.success) {
        showSnackbar("Sent to NABL successfully", "success");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        showSnackbar(response.data.message || "Failed to send", "error");
      }
    } catch (err) {
      console.error("Error sending to NABL:", err);
      showSnackbar("Error sending to NABL", "error");
    }
  };

  const handleSendToCab = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/send-to-cab`,
        {
          appId: appId,
          orgId: orgId,
        },
      );

      if (response.data.success) {
        showSnackbar("Sent to CAB successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 4000);
      } else {
        showSnackbar(response.data.message || "Failed to send", "error");
      }
    } catch (err) {
      console.error("Error sending to CAB:", err);
      showSnackbar("Error sending to CAB", "error");
    }
  };

  const handleCloseNC = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/update-stage`,
        {
          appId: appId,
          orgId: orgId,
          stage: "15c",
        },
      );

      if (response.data.success) {
        showSnackbar("NC closed successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        showSnackbar(response.data.message || "Failed to close NC", "error");
      }
    } catch (err) {
      console.error("Error closing NC:", err);
      showSnackbar("Error closing NC", "error");
    }
  };

  const handleGoForAssessment = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/update-stage`,
        {
          appId: appId,
          orgId: orgId,
          stage: "19",
        },
      );

      if (response.data.success) {
        showSnackbar("Application moved to assessment successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        showSnackbar(
          response.data.message || "Failed to move application to assessment",
          "error",
        );
      }
    } catch (err) {
      console.error("Error moving application to assessment:", err);
      showSnackbar("Error moving application to assessment", "error");
    }
  };

  const handleGenerateForm11 = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/update-stage`,
        {
          appId: appId,
          orgId: orgId,
          stage: "15c",
        },
      );

      if (response.data.success) {
        showSnackbar("Form 11 generated successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        showSnackbar(
          response.data.message || "Failed to generate Form 11",
          "error",
        );
      }
    } catch (err) {
      console.error("Error generating Form 11:", err);
      showSnackbar("Error generating Form 11", "error");
    }
  };

  const handleProcessCompleted = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/change-in-cab-permises/update-stage`,
        {
          appId: appId,
          orgId: orgId,
          stage: "15d",
        },
      );

      if (response.data.success) {
        showSnackbar("Process completed successfully", "success");
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        showSnackbar(
          response.data.message || "Failed to complete the process",
          "error",
        );
      }
    } catch (err) {
      console.error("Error completing the process:", err);
      showSnackbar("Error completing the process", "error");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Change In CAB's Premises Form...
        </Typography>
      </Box>
    );
  }

  // Render functions for each legal entity type
  const renderLegalEntityForm = () => {
    if (legalEntityType === "Proprietorship") {
      return renderProprietorshipForm();
    } else if (legalEntityType === "Partnership") {
      return renderPartnershipForm();
    } else if (legalEntityType === "LLP") {
      return renderLLPForm();
    } else if (legalEntityType === "Companies Act") {
      return renderCompaniesActForm();
    } else if (legalEntityType === "Public Ltd") {
      return renderPublicLtdForm();
    } else if (legalEntityType === "Government") {
      return renderGovernmentForm();
    } else if (legalEntityType === "Trust") {
      return renderTrustForm();
    } else if (legalEntityType === "Society") {
      return renderSocietyForm();
    } else if (legalEntityType === "One Person Company") {
      return renderOnePersonCompanyForm();
    } else {
      return (
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            Please select a legal entity type to view the form.
          </Alert>
        </Grid>
      );
    }
  };

  const renderProprietorshipForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Legal Entity Name"
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          value="DR"
          disabled
          placeholder="DR"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f5f5f5",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Name of the Owner"
          value={ownerName}
          onChange={(e) => setOwnerName(e.target.value)}
          placeholder="Enter Owner Name"
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      {/* MCI/NMC Details Section */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            mb: 2,
            color: "#667eea",
            fontWeight: 600,
            borderBottom: "2px solid #667eea",
            paddingBottom: 1,
          }}
        >
          MCI/NMC Details{" "}
          <Typography
            component="span"
            sx={{ fontSize: "0.8rem", fontWeight: 400, color: "#ff9800" }}
          >
            (Either MCI/NMC details OR State registration details must be
            provided)
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="MCI/NMC Registered Number"
          value={mciRegNumber}
          onChange={(e) => setMciRegNumber(e.target.value)}
          placeholder="Enter MCI/NMC Number"
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="legalIdentityDoc1"
          label="MCI/NMC Certificate"
          orgId={orgId}
          section="change-premises"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={legalIdentityDoc1}
          disabled={isReadOnly}
        />
      </Grid>

      {/* State Registration Details Section */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            mb: 2,
            color: "#667eea",
            fontWeight: 600,
            borderBottom: "2px solid #667eea",
            paddingBottom: 1,
          }}
        >
          State Registration Details{" "}
          <Typography
            component="span"
            sx={{ fontSize: "0.8rem", fontWeight: 400, color: "#ff9800" }}
          >
            (Alternative to MCI/NMC details above)
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="State Registration Certificate Number"
          value={stateRegistrationNumber}
          onChange={(e) => setStateRegistrationNumber(e.target.value)}
          placeholder="Enter Certificate Number"
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="legalIdentityDoc2"
          label="State Registration Certificate"
          orgId={orgId}
          section="change-premises"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={legalIdentityDoc2}
          disabled={isReadOnly}
        />
      </Grid>
    </>
  );

  const renderPartnershipForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Legal Entity Name"
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          value="DR"
          disabled
          placeholder="DR"
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#f5f5f5",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Name of the Owner"
          value={partnershipOwnerName}
          onChange={(e) => setPartnershipOwnerName(e.target.value)}
          placeholder="Enter Owner Name"
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="partnershipDeedFile"
          label="Upload Partnership Deed"
          orgId={orgId}
          section="change-premises"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={partnershipDeedFile}
          disabled={isReadOnly}
        />
      </Grid>

      {/* MCI/NMC Details Section */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            mb: 2,
            color: "#667eea",
            fontWeight: 600,
            borderBottom: "2px solid #667eea",
            paddingBottom: 1,
          }}
        >
          MCI/NMC Details{" "}
          <Typography
            component="span"
            sx={{ fontSize: "0.8rem", fontWeight: 400, color: "#ff9800" }}
          >
            (Either MCI/NMC details OR State registration details must be
            provided)
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="MCI/NMC Registered Number"
          value={mciRegNumber}
          onChange={(e) => setMciRegNumber(e.target.value)}
          placeholder="Enter MCI/NMC Number"
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="legalIdentityDoc1"
          label="MCI/NMC Certificate"
          orgId={orgId}
          section="change-premises"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={legalIdentityDoc1}
          disabled={isReadOnly}
        />
      </Grid>

      {/* State Registration Details Section */}
      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            mb: 2,
            color: "#667eea",
            fontWeight: 600,
            borderBottom: "2px solid #667eea",
            paddingBottom: 1,
          }}
        >
          State Registration Details{" "}
          <Typography
            component="span"
            sx={{ fontSize: "0.8rem", fontWeight: 400, color: "#ff9800" }}
          >
            (Alternative to MCI/NMC details above)
          </Typography>
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="State Registration Certificate Number"
          value={stateRegistrationNumber}
          onChange={(e) => setStateRegistrationNumber(e.target.value)}
          placeholder="Enter Certificate Number"
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="legalIdentityDoc2"
          label="State Registration Certificate"
          orgId={orgId}
          section="change-premises"
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={legalIdentityDoc2}
          disabled={isReadOnly}
        />
      </Grid>
    </>
  );

  const renderLLPForm = () => (
    <>
      <Grid item xs={12} md={5}>
        <TextField
          fullWidth
          required
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={7}>
        <FileUploadComponent
          fieldName="llpCertificateFile"
          label="Upload LLP Certificate"
          orgId={orgId}
          section="change-premises"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={llpCertificateFile}
          disabled={isReadOnly}
        />
      </Grid>
    </>
  );

  const renderCompaniesActForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="companiesActCertificateFile"
          label="Upload Companies Act Certificate"
          orgId={orgId}
          section="change-premises"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={companiesActCertificateFile}
          disabled={isReadOnly}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            mb: 2,
            color: "#667eea",
            fontWeight: 600,
            borderBottom: "2px solid #667eea",
            paddingBottom: 1,
          }}
        >
          Address Details
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Registered Office Address"
          value={legalIdentity_Address}
          onChange={(e) => setLegalIdentity_Address(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="State"
          value={legalIdentity_State}
          onChange={(e) => setLegalIdentity_State(e.target.value)}
          placeholder="Enter State..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Pincode"
          value={legalIdentity_Pincode}
          onChange={(e) =>
            handleNumericChange("legalIdentity_Pincode", e.target.value, 6)
          }
          placeholder="Enter 6-digit pincode"
          inputProps={{ maxLength: 6 }}
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>
    </>
  );

  const renderPublicLtdForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FileUploadComponent
          fieldName="publicLtdCertificateFile"
          label="Upload Public Ltd Certificate"
          orgId={orgId}
          section="change-premises"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={publicLtdCertificateFile}
          disabled={isReadOnly}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography
          variant="h6"
          sx={{
            mt: 2,
            mb: 2,
            color: "#667eea",
            fontWeight: 600,
            borderBottom: "2px solid #667eea",
            paddingBottom: 1,
          }}
        >
          Office Address
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Registered Office Address"
          value={legalIdentity_Address}
          onChange={(e) => setLegalIdentity_Address(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>
    </>
  );

  const renderGovernmentForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "#666", fontWeight: 500 }}
          >
            Are you an Autonomous Body?
            <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControlLabel
              control={
                <Radio
                  checked={governmentIsAutonomous === "Yes"}
                  onChange={(e) => setGovernmentIsAutonomous("Yes")}
                  disabled={isReadOnly}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={governmentIsAutonomous === "No"}
                  onChange={(e) => setGovernmentIsAutonomous("No")}
                  disabled={isReadOnly}
                />
              }
              label="No"
            />
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="governmentCertificateFile"
          label="Upload Gazette or Government Notification or self-Declaration on letter head by Head of the organization"
          orgId={orgId}
          section="change-premises"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={governmentCertificateFile}
          disabled={isReadOnly}
        />
      </Grid>
    </>
  );

  const renderTrustForm = () => (
    <>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          required
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="trustCertificateFile"
          label="Upload Trust Certificate/Deed"
          orgId={orgId}
          section="change-premises"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={trustCertificateFile}
          disabled={isReadOnly}
        />
      </Grid>
    </>
  );

  const renderSocietyForm = () => (
    <>
      <Grid item xs={12} md={5}>
        <TextField
          fullWidth
          required
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="societyCertificateFile"
          label="Upload Society Certificate"
          orgId={orgId}
          section="change-premises"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={societyCertificateFile}
          disabled={isReadOnly}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "#666", fontWeight: 500 }}
          >
            Are you an Autonomous Body?
            <span style={{ color: "red", fontWeight: "bold" }}>*</span>
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControlLabel
              control={
                <Radio
                  checked={societyIsAutonomous === "Yes"}
                  onChange={(e) => setSocietyIsAutonomous("Yes")}
                  disabled={isReadOnly}
                />
              }
              label="Yes"
            />
            <FormControlLabel
              control={
                <Radio
                  checked={societyIsAutonomous === "No"}
                  onChange={(e) => setSocietyIsAutonomous("No")}
                  disabled={isReadOnly}
                />
              }
              label="No"
            />
          </Box>
        </Box>
      </Grid>
    </>
  );

  const renderOnePersonCompanyForm = () => (
    <>
      <Grid item xs={12} md={5}>
        <TextField
          fullWidth
          label="Legal Entity Name"
          value={legalIdentityName}
          onChange={(e) => setLegalIdentityName(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Registered Office Address"
          value={legalIdentity_Address}
          onChange={(e) => setLegalIdentity_Address(e.target.value)}
          placeholder="Enter Details..."
          disabled={isReadOnly}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: 2,
            },
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FileUploadComponent
          fieldName="opcCertificateFile"
          label="Upload One Person Company Certificate"
          orgId={orgId}
          section="change-premises"
          required={true}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          maxSize={5}
          onChange={handleS3FileUpload}
          initialFile={opcCertificateFile}
          disabled={isReadOnly}
        />
      </Grid>
    </>
  );

  const isReadOnly = stage === "15" || stage === "15b" || stage === "15c";
  const showLocationPanel = shiftingLocation === "Same District";

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc", pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          <Breadcrumbs
            sx={{
              mb: 2,
              "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBackClick}
              sx={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { color: "white" },
              }}
            >
              Applications
            </Link>
            <Typography variant="body2" sx={{ color: "white" }}>
              Change In CAB's Premises
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Change In CAB's Premises
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ px: 3 }}>
        {/* Basic Laboratory Details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Laboratory Details
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name of the Laboratory"
                  value={cabName}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Type of Laboratory</InputLabel>
                  <Select value={cabType} disabled label="Type of Laboratory">
                    <MenuItem value="0">--Select Type--</MenuItem>
                    <MenuItem value="Diagnostic">
                      Medical Testing Laboratory
                    </MenuItem>
                    <MenuItem value="Medical Imaging">Medical Imaging</MenuItem>
                    <MenuItem value="Both">
                      Medical Testing Laboratory + Medical Imaging
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select value={country} disabled label="Country">
                    <MenuItem value="101">India</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>State</InputLabel>
                  <Select value={state} disabled label="State">
                    <MenuItem value="0">--Select State--</MenuItem>
                    {states.map((s) => (
                      <MenuItem key={s.id} value={s.id?.toString() || ""}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>City</InputLabel>
                  <Select value={city} disabled label="City">
                    <MenuItem value="0">--Select City--</MenuItem>
                    {cities.map((c) => (
                      <MenuItem key={c.id} value={c.id?.toString() || ""}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={address}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="District"
                  value={districtName || district}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pin Code"
                  value={pincode}
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Mobile No"
                  value={mobile}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Landline No"
                    value={landline}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fax No"
                    value={fax}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
               <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="E-mail"
                  value={email}
                  InputProps={{ readOnly: true }}
                />
              </Grid> */}
            </Grid>
          </CardContent>
        </Card>

        {/* Shifting Location */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Shifting of Location <span style={{ color: "red" }}>*</span>
                </Typography>
                <RadioGroup
                  row
                  value={shiftingLocation}
                  onChange={(e) => handleShiftingLocationChange(e.target.value)}
                >
                  <FormControlLabel
                    value="Same District"
                    control={<Radio disabled={isReadOnly} />}
                    label="Same City/District"
                  />
                  <FormControlLabel
                    value="Another District"
                    control={<Radio disabled={isReadOnly} />}
                    label="Another City/District"
                  />
                </RadioGroup>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* New Location Details - Only if Same District */}
        {showLocationPanel && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                New Location Details
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="New Address"
                    value={newAddress}
                    onChange={(e) => setNewAddress(e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="GSTIN"
                    value={newGstin}
                    onChange={(e) => setNewGstin(e.target.value)}
                    disabled={isReadOnly}
                    helperText="(if not applicable then write NA)"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>City</InputLabel>
                    <Select
                      value={newCity}
                      onChange={(e) => setNewCity(e.target.value)}
                      label="City"
                    >
                      <MenuItem value="0">--Select City--</MenuItem>
                      {newCities.map((c) => (
                        <MenuItem key={c.id} value={c.id?.toString() || ""}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>District</InputLabel>
                    <Select
                      value={newDistrict}
                      onChange={(e) => setNewDistrict(e.target.value)}
                      label="District"
                      disabled={isReadOnly}
                    >
                      <MenuItem value="">--Select District--</MenuItem>
                      {newDistricts.map((d) => (
                        <MenuItem
                          key={d.District_Code || d.id}
                          value={
                            d.District_Code?.toString() ||
                            d.id?.toString() ||
                            ""
                          }
                        >
                          {d.District_Name_Eng || d.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Pin Code"
                    value={newPincode}
                    onChange={(e) => setNewPincode(e.target.value)}
                    disabled={isReadOnly}
                    inputProps={{ maxLength: 6 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {/* <TextField
                      label="Code"
                      value={newCountryCode}
                      onChange={(e) => setNewCountryCode(e.target.value)}
                      disabled={isReadOnly}
                      sx={{ width: "100px" }}
                      inputProps={{ maxLength: 5 }}
                    /> */}
                    <TextField
                      fullWidth
                      required
                      label="Contact No"
                      value={newContact}
                      onChange={(e) => setNewContact(e.target.value)}
                      disabled={isReadOnly}
                      inputProps={{ maxLength: 11 }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {/* <TextField
                      label="Code"
                      value={newFaxCode}
                      onChange={(e) => setNewFaxCode(e.target.value)}
                      disabled={isReadOnly}
                      sx={{ width: "100px" }}
                      inputProps={{ maxLength: 5 }}
                    /> */}
                    <TextField
                      fullWidth
                      label="Fax No"
                      value={newFaxNo}
                      onChange={(e) => setNewFaxNo(e.target.value)}
                      disabled={isReadOnly}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="E-mail"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>

                {/* Is this a change in Legal Entity Question */}
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ mb: 2, color: "#666", fontWeight: 500 }}
                    >
                      Is this a change in Legal Entity?
                      <span style={{ color: "red", fontWeight: "bold" }}>
                        *
                      </span>
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isChangeLegalEntity === "Yes"}
                            onChange={(e) =>
                              handleIsChangeLegalEntityChange("Yes")
                            }
                            disabled={isReadOnly}
                          />
                        }
                        label="Yes"
                      />
                      <FormControlLabel
                        control={
                          <Radio
                            checked={isChangeLegalEntity === "No"}
                            onChange={(e) =>
                              handleIsChangeLegalEntityChange("No")
                            }
                            disabled={isReadOnly}
                          />
                        }
                        label="No"
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Document Adequacy - New Location Details */}
        {showLocationPanel && (
          <AdequacyCheckSection
            sectionId="new-location-details"
            title="New Location Details"
            applicationStage={stage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            handleAdequacySubmitted={handleAdequacySubmitted}
            loading={loading}
            currentUserId={localStorage.getItem("orgId")}
          />
        )}

        {/* Legal Identity Details Card */}
        {showLocationPanel && isChangeLegalEntity === "Yes" && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 2, mt: 2 }}
                  >
                    Legal Identity Details
                  </Typography>
                </Grid>

                {isChangeLegalEntity === "Yes" && (
                  <>
                    {/* Legal Entity Type Dropdown */}
                    <Grid item xs={12} md={6}>
                      <FormControl
                        fullWidth
                        required
                        error={!!validationErrors["legalEntityType"]}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                            borderRadius: 2,
                            "&:hover": {
                              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                            },
                            "&.Mui-focused": {
                              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
                            },
                            "&.Mui-error": {
                              borderColor: "#f44336",
                              "&:hover": {
                                boxShadow: "0 4px 8px rgba(244, 67, 54, 0.2)",
                              },
                              "&.Mui-focused": {
                                boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
                              },
                            },
                          },
                        }}
                        disabled={isReadOnly}
                      >
                        <InputLabel>Legal Entity Type</InputLabel>
                        <Select
                          value={legalEntityType || ""}
                          onChange={(e) =>
                            handleLegalEntityChange(
                              "legalEntityType",
                              e.target.value,
                            )
                          }
                          label="Legal Entity Type"
                        >
                          <MenuItem value="">-- Select --</MenuItem>
                          <MenuItem value="Proprietorship">
                            Proprietorship
                          </MenuItem>
                          <MenuItem value="Partnership">Partnership</MenuItem>
                          <MenuItem value="LLP">LLP</MenuItem>
                          <MenuItem value="Companies Act">
                            Companies Act
                          </MenuItem>
                          <MenuItem value="Public Ltd">Public Ltd</MenuItem>
                          <MenuItem value="Government">Government</MenuItem>
                          <MenuItem value="Trust">Trust</MenuItem>
                          <MenuItem value="Society">Society</MenuItem>
                          <MenuItem value="One Person Company">
                            One Person Company
                          </MenuItem>
                        </Select>
                        {validationErrors["legalEntityType"] && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#f44336", mt: 1, ml: 2 }}
                          >
                            {validationErrors["legalEntityType"]}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Proprietorship Info - Show only for Proprietorship */}
                    {legalEntityType === "Proprietorship" && (
                      <Grid item xs={12}>
                        <Alert
                          severity="info"
                          sx={{
                            backgroundColor: "#e3f2fd",
                            border: "1px solid #bbdefb",
                            borderRadius: 2,
                            "& .MuiAlert-icon": {
                              color: "#1976d2",
                            },
                          }}
                        >
                          The Proprietorship option is only open to Doctors
                          registered with the MCI/NMC and/or the State Medical
                          Council.
                        </Alert>
                      </Grid>
                    )}

                    {/* Registration Requirement Info - Show for Proprietorship and Partnership */}
                    {requiresRegistrationValidation(legalEntityType) && (
                      <Grid item xs={12}>
                        <Alert
                          severity="warning"
                          sx={{
                            backgroundColor: "#fff3e0",
                            border: "1px solid #ffcc02",
                            borderRadius: 2,
                            "& .MuiAlert-icon": {
                              color: "#f57c00",
                            },
                          }}
                        >
                          <strong>Registration Requirement:</strong> You must
                          provide either MCI/NMC registration details OR State
                          registration details. At least one complete set (both
                          number and certificate) is mandatory.
                        </Alert>
                      </Grid>
                    )}

                    {/* Dynamic Legal Entity Details Card */}
                    {legalEntityType && (
                      <Grid item xs={12}>
                        <Paper
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Grid container spacing={3}>
                            {renderLegalEntityForm()}
                          </Grid>
                        </Paper>
                      </Grid>
                    )}
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}
        {/* Additional Documents Card */}
        {showLocationPanel && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3}>
                {/* Management System Document */}
                <Grid item xs={12}>
                  <FileUploadComponent
                    fieldName="managementFile"
                    label="Upload Management System Document"
                    orgId={orgId}
                    section="change-premises"
                    accept=".pdf,.PDF"
                    maxSize={5}
                    onChange={(field, file) => setManagementFile(file)}
                    initialFile={managementFile}
                    required
                    disabled={isReadOnly}
                  />
                </Grid>

                {/* Declaration File */}
                {showDeclarationUpload && (
                  <Grid item xs={12}>
                    <FileUploadComponent
                      fieldName="declarationFile"
                      label="Upload Declaration on letterhead by head of the organization stating that implementation of management system has been verified through Internal audit and the same has been reviewed during management review meeting"
                      orgId={orgId}
                      section="change-premises"
                      accept=".pdf,.PDF"
                      maxSize={5}
                      onChange={(field, file) => setDeclarationFile(file)}
                      initialFile={declarationFile}
                      required
                      disabled={isReadOnly}
                    />
                  </Grid>
                )}

                {/* No Claim Checkbox */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={noClaim}
                        onChange={(e) => setNoClaim(e.target.checked)}
                        disabled={isReadOnly}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        CAB shall not claim to be NABL accredited and shall not
                        use NABL symbol till the change is accepted by NABL.{" "}
                        <span style={{ color: "red" }}>*</span>
                      </Typography>
                    }
                  />
                </Grid>

                {/* Remarks */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Remarks, if any"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    disabled={isReadOnly}
                  />
                </Grid>

                {/* Remarks File Upload */}
                {remarks && (
                  <Grid item xs={12}>
                    <FileUploadComponent
                      fieldName="remarksFile"
                      label="Upload File along with Remarks"
                      orgId={orgId}
                      section="change-premises"
                      accept=".pdf,.PDF"
                      maxSize={5}
                      onChange={(field, file) => setRemarksFile(file)}
                      initialFile={remarksFile}
                      disabled={isReadOnly}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Document Adequacy - Legal Identity Details */}
        {showLocationPanel && isChangeLegalEntity === "Yes" && (
          <AdequacyCheckSection
            sectionId="legal-identity-details"
            title="Legal Identity Details"
            applicationStage={stage}
            orgId={orgId}
            appId={appId}
            adequacyChecks={adequacyChecks}
            handleAdequacyCheck={handleAdequacyCheck}
            handleAdequacyRemarks={handleAdequacyRemarks}
            handleAdequacySubmit={handleAdequacySubmit}
            handleAdequacySubmitted={handleAdequacySubmitted}
            loading={loading}
            currentUserId={localStorage.getItem("orgId")}
          />
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 4 }}>
          {showLocationPanel && !isReadOnly && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSaveDetails}
              size="large"
            >
              Update
            </Button>
          )}

          {showSubmitButton && (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setDialogType("final-submit");
                setShowPasswordDialog(true);
              }}
              size="large"
            >
              Final Submit
            </Button>
          )}

          {showPaymentButton && (
            <Button
              variant="contained"
              color="info"
              onClick={() => {
                setDialogType('payment');
                setShowPasswordDialog(true);
              }}
              size="large"
            >
              Proceed for Payment
            </Button>
          )}

          {/* {userTypeId === "4" && (stage === "15" || stage === "15b") && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => {
                setDialogType("close-nc");
                setShowPasswordDialog(true);
              }}
              size="large"
            >
              Close NC
            </Button>
          )} */}
          {userTypeId === "4" &&
            (stage === "15" || stage === "15b") &&
            // Show Send to CAB button only when adequacy checks are submitted AND at least one is Inadequate
            adequacySubmitted["new-location-details"] &&
            (isChangeLegalEntity === "No" ||
              adequacySubmitted["legal-identity-details"]) &&
            (adequacyChecks["new-location-details"]?.status === "Inadequate" ||
              (isChangeLegalEntity === "Yes" &&
                adequacyChecks["legal-identity-details"]?.status ===
                  "Inadequate")) && (
              <Button
                variant="contained"
                color="info"
                onClick={() => {
                  setDialogType("send-to-cab");
                  setShowPasswordDialog(true);
                }}
                size="large"
              >
                Send to CAB
              </Button>
            )}
          {userTypeId !== "4" && stage === "15a" && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setDialogType("send-to-nabl");
                setShowPasswordDialog(true);
              }}
              size="large"
            >
              Send to NABL
            </Button>
          )}

          {userTypeId === "4" && (stage === "15" || stage === "15b") && adequacySubmitted["new-location-details"] &&
            (isChangeLegalEntity === "No" ||
              adequacySubmitted["legal-identity-details"]) &&
            // Check if all sections are Adequate or To be Verify
            (() => {
              const sectionsToCheck = ["new-location-details"];
              if (isChangeLegalEntity === "Yes") {
                sectionsToCheck.push("legal-identity-details");
              }
              return sectionsToCheck.every(sectionId => {
                const status = adequacyChecks[sectionId]?.status;
                return status === "Adequate" || status === "To be Verify";
              });
            })() && (
            <>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setDialogType("generate-form-11");
                  setShowPasswordDialog(true);
                }}
                size="large"
              >
                Generate Form 11
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setDialogType("go-for-assessment");
                  setShowPasswordDialog(true);
                }}
                size="large"
              >
                Go For Assessment
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setDialogType("process-completed");
                  setShowPasswordDialog(true);
                }}
                size="large"
              >
                Process Completed
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Password Dialog */}
      <Dialog
        open={showPasswordDialog}
        onClose={() => setShowPasswordDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Enter Password</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
          <Button
            onClick={handlePasswordSubmit}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      {/* <TerminateApplicationDialog appId={appId} stage={stage} /> */}
    </Box>
  );
};

export default ChangeINCabPermises;
